import React, { useEffect, useRef, useState } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import '.././App.css';

const RealTimeSpeechToText = () => {
  const [transcription, setTranscription] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognizerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0); // total elapsed seconds
  const [params, setParams] = useState(null);
  const intervalRef = useRef(null);
  const opInProgressRef = useRef(false)
  const [micPermission, setMicPermission] = useState(null);
  const [showEnableMicMessage, setShowEnableMicMessage] = useState(false);
  
  useEffect(() => {
    window.addEventListener('message', handleMessage, false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (isListening && !isPaused) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    }

    return () => {
      window.removeEventListener('message', handleMessage);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };    
  }, [isListening, isPaused]);

  const stopRecognition = () => {
    try{
      if (recognizerRef.current) {
        setElapsedSeconds(0);
        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            console.log("Recognition stopped");
            setIsListening(false);
            recognizerRef.current = null;
          },
          (err) => console.error("Error stopping recognition: ", err)
        );
      }
    }catch(e){
      console.error("RA(AS): stopRecognition error: " + e.message);
    }    
  };

  const pauseRecognition = () => {
    try{
      if (recognizerRef.current && !isPaused && !opInProgressRef.current) {
        opInProgressRef.current = true;
        setIsPaused(true);

        recognizerRef.current.stopContinuousRecognitionAsync(
          () => {
            console.log("Recognition paused");
            opInProgressRef.current = false;
          },
          (err) => { 
            console.error("Error stopping recognition: ", err);
            opInProgressRef.current = false;
          }
        );
      }
    } catch(e) {
      console.error("RA(AS): pauseRecognition error: " + e.message);
    }
  };

  const clearTranscription = ()=>{
    setTranscription("");
  };

  const handleMessage = async (event) => {
    if (event.data.type) {
      console.log("RA(AS): " + event.data.type);
      if (event.data.type === 'initSpeechToText') {
        try{
          setParams(event.data.data);
          
          if (recognizerRef.current) {
            console.log("RA(AS): No need to initialize");
          }          
        } catch (e) {
          console.error(e);
        }
      } else if (event.data.type === 'pauseSpeechToText') { 
        pauseRecognition();
      } else if (event.data.type === 'stopSpeechToText') { 
        stopRecognition();
      }
    }
  };

  function initListener() {
    try{
      let speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        params.token,
        params.region
      );
      speechConfig.speechRecognitionLanguage = params.language;


      if (params?.enableAutoPunctuation === true) {
        speechConfig.enableAutoPunctuation = true;
      }

      let audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      let recognizerInstance = new SpeechSDK.SpeechRecognizer(
        speechConfig,
        audioConfig
      );

      recognizerRef.current = recognizerInstance;
      recognizerRef.current.recognizing = (sender, event) => {
        console.log(`Recognizing: ${event.result.text}`);
        window.parent.postMessage({ data: { type: "speechToTextRecognizing", data: event.result.text } }, "*");
      };

      recognizerRef.current.recognized = (sender, event) => {
        if (event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          console.log(`Final result: ${event.result.text}`);
          //setTranscription((prev) => prev + event.result.text + " ");
          window.parent.postMessage({ data: { type: "speechToText", data: event.result.text } }, "*");
        } else if (event.result.reason === SpeechSDK.ResultReason.NoMatch) {
          //console.log("NoMatch: Speech could not be recognized.");
        }
      };
      console.log ("RA(AS): Speech to text initialized");
    }catch(e){
      console.error("RA(AS): initListener error: " + e.message);
    }    
  }

  async function startListener(){
    try{
      if (!navigator.permissions || !navigator.permissions.query) {
        setMicPermission(null);
        window.parent.postMessage({ data: { type: "speechToTextError", data: "Microphone access is blocked. Please enable microphone permission in your browser settings to use voice-to-text"} }, "*");
        return;
      }

      const status = await navigator.permissions.query({ name: "microphone" });
      if (status.state === "denied") {
        window.parent.postMessage({ data: { type: "speechToTextError", data: "Microphone access is blocked. Please enable microphone permission in your browser settings to use voice-to-text"} }, "*");
        return;
      }

      if (!recognizerRef.current){
        initListener();
      }
      
      if (!opInProgressRef.current){
        opInProgressRef.current = true;
        recognizerRef.current.startContinuousRecognitionAsync(
          () => {
            console.log("Recognition started");
            setIsListening(true);
            opInProgressRef.current = false;
          },
          (err) => {
            console.error("Error starting recognition: ", err);
            stopRecognition();
            opInProgressRef.current = false;
          }
        );
      }

      setIsPaused(false);
      console.log("RA(AS): Started Listening");
    }catch(e){
      console.error("RA(AS): startListener error: " + e.message);
    }    
  }

  const formatMMSS = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const mm = String(mins).padStart(2, "0");
    const ss = String(secs).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  async function queryMicPermission() {
    if (!navigator.permissions || !navigator.permissions.query) {
      // Permissions API not supported; leave micPermission null and fall back to getUserMedia on user click
      setMicPermission(null);
      return;
    }

    try {
      const status = await navigator.permissions.query({ name: "microphone" });
      setMicPermission(status.state); // "granted" | "denied" | "prompt"
      if (status.state === "denied") {
        setShowEnableMicMessage(true);
      }

      status.onchange = () => {
        // update when user changes in browser UI
        setMicPermission(status.state);
        if (status.state !== "denied") setShowEnableMicMessage(false);
      };
    } catch (e) {
      // Some browsers may throw for cross-origin iframes or not support microphone name
      setMicPermission(null);
    }
  }

  return (
    <div className="relative">
      {!isListening && (
        <div className="start-voice-icon">
          <img
            src={"https://static-content.insightscrm.com/ib/images/voice-mic.svg"}
            title="Voice to text"
            className="absolute controls"
            alt="Start voice"
            height="30"
            width="30"
            onClick={startListener}
          />
        </div>
      )}

      {isListening && (
        <div className="record-box">
          {isPaused && (
            <img className="vertical-mid" src={"https://static-content.insightscrm.com/ib/images/voice-pause-icon.svg"} />
          )}
          {!isPaused && (
            <img className="vertical-mid" src={"https://static-content.insightscrm.com/ib/images/voice-listening-icon.svg"} />
          )}
          <div style={{marginRight: "auto", width: "100%" }}>
            <label>{!isListening ? "Click to Start" : isPaused ? "Paused" : "I'm Listening..."}</label>
          </div>
          <div>{formatMMSS(elapsedSeconds)}</div>          
          <div className="controls">
            {!isPaused && (
              <div className="pause-btn">
                <img 
                  src={"https://static-content.insightscrm.com/ib/images/voice-on.svg"} 
                  title="Pause"
                  className="cursor-pointer mx-2 btn-voice-pause vertical-mid"
                  alt="Pause"
                  height="28"
                  width="28"
                  onClick={pauseRecognition}
                />
              </div>
            )}
            {isPaused && (
              <div className="voice-resume">
                <img 
                  src={"https://static-content.insightscrm.com/ib/images/voice-resume.svg"} 
                  title="Resume"
                  style={{cursor: "pointer"}}
                  className="mx-2 btn-voice-pause vertical-mid"
                  alt="Resume"
                  height="28"
                  width="28"
                  onClick={startListener}
                />
              </div>
            )}

            <div className="stop-btn">
              <img 
                src={"https://static-content.insightscrm.com/ib/images/voice-stop.svg"} 
                style={{cursor: "pointer"}}
                title="Stop"
                className="cursor-pointer vertical-mid"
                alt="Stop"
                height="28"
                width="28"
                onClick={stopRecognition}
              />
            </div> 
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeSpeechToText;